import { envConfig } from '#constants/envConfig';
import { nativeOAuthProviderPath } from './nativeOAuthProviderPath';
import { rememberNativeOAuthReturn } from './nativeOAuthReturn';

/**
 * OAuth for the native (Capacitor) shell.
 *
 * OAuth providers (Google especially) refuse to authenticate inside an embedded
 * WebView, so the flow must run in the system browser (Custom Tabs). We open
 * `${apiOrigin}/oauth2/authorization/{provider}?returnUrl=/native/oauth-callback`
 * there; teamplay-user recognises the sentinel returnUrl and, on success, redirects
 * to the custom-scheme deep link `gg.teamplay.twa://oauth-callback?token=…`, which
 * this app catches (see useNativeOAuthDeepLink).
 *
 * Everything is gated behind `envConfig.IS_NATIVE` (build-time constant) so the web
 * build tree-shakes the dynamic `@capacitor/browser` import away entirely.
 */

// Must match OAuth2RedirectUtils.NATIVE_RETURN_PATH / NATIVE_DEEPLINK in teamplay-user.
export const NATIVE_OAUTH_RETURN_PATH = '/native/oauth-callback';
export const NATIVE_OAUTH_DEEPLINK_PREFIX = 'gg.teamplay.twa://oauth-callback';

export const canUseNativeOAuth = () => envConfig.IS_NATIVE;

export async function startNativeOAuth(provider: string): Promise<void> {
  if (!envConfig.IS_NATIVE || !envConfig.API_URL) {
    return;
  }
  // Диплинк возврата исходного адреса не несёт, поэтому запоминаем его здесь. Иначе человек,
  // открывший приглашение в приложении, после входа оказывается на дашборде, а место в сборе
  // так и остаётся незанятым (см. nativeOAuthReturn).
  if (typeof window !== 'undefined') {
    rememberNativeOAuthReturn(`${window.location.pathname}${window.location.search}`);
  }
  // Одна карта на оба нативных флоу — вход и привязку. Их было две, и вторая отстала:
  // Bungie завели в UI, кнопка входа в приложении молча ничего не делала (здесь путь просто
  // не находился и функция выходила без единого следа).
  const path = nativeOAuthProviderPath(provider);
  if (!path) {
    return;
  }
  // OAuth lives on the API host (same as GraphQL), not the localhost app origin.
  const origin = new URL(envConfig.API_URL).origin;
  const url = `${origin}/oauth2/authorization/${path}?returnUrl=${encodeURIComponent(
    NATIVE_OAUTH_RETURN_PATH
  )}`;

  const { Browser } = await import('@capacitor/browser');
  await Browser.open({ url });
}
